import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { ActionButton } from 'core/components';
import { AccessPermissions, ActionButtonType } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { IPartFinderReel } from 'partFinder/types';
import { QuoteEntityType } from 'quotes/enums';
import { Color, Variant } from 'theme/enums';

import { AddToQuoteOrder } from './AddToQuoteOrder';

interface IProps {
  reels: IPartFinderReel[];
  onSubmitSuccess?: () => void;
}

// The Add To... menu the results grid shows while reels are ticked (Figma 2634:765293). Customer
// Order is only offered to users who may create orders.
export const AddToQuoteOrderButton = ({ reels, onSubmitSuccess }: IProps) => {
  const [entityType, setEntityType] = useState<QuoteEntityType | null>(null);

  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();
  const { isLoadingTopNotificationDisplayed } = useNotificationContext();

  const menuItems = useMemo(
    () => [
      {
        label: t('buttonLabelAddToCustomerQuote', { entityType: QuoteEntityType.QUOTE }),
        onClick: () => setEntityType(QuoteEntityType.QUOTE),
      },
      ...(hasPermission(AccessPermissions.ACCESS_NEW_ORDER)
        ? [
            {
              label: t('buttonLabelAddToCustomerQuote', { entityType: QuoteEntityType.ORDER }),
              onClick: () => setEntityType(QuoteEntityType.ORDER),
            },
          ]
        : []),
    ],
    [hasPermission, t]
  );

  return (
    <>
      <ActionButton
        action={{
          type: ActionButtonType.MENU,
          label: t('buttonLabelAddTo'),
          color: Color.NEUTRAL,
          variant: Variant.OUTLINED,
          menuItems,
          disabled: isLoadingTopNotificationDisplayed,
        }}
      />

      {entityType && (
        <AddToQuoteOrder
          entityType={entityType}
          reels={reels}
          onClose={() => setEntityType(null)}
          onSubmitSuccess={onSubmitSuccess}
        />
      )}
    </>
  );
};
